import React from 'react';
import { NativeFormElementPanelType } from './add-panel';
import { Block, NativeFormBlockContent } from '../types';
import { NativeFormConfig, NativeFormElement, NativeFormElementType, NativeFormSupportedElementTypes } from './store';
import { Nullable } from 'src/types/nullable.type';
import capitalize from 'lodash/capitalize';
import { ControlPanelToggleButton } from '../components/control-panel/toggle-button';
import { NativeFormElementConfigFormFactory } from './element-config-form.factory';
import { NativeFormConfigBlockForm } from './config/block-form';

interface Props {
  type: Nullable<NativeFormElementPanelType>;
  block: Block;
  formElement: Nullable<NativeFormElement>;
  onAddClick: (elementType: NativeFormElementType) => void;
  onUpdateField: (fields: NativeFormElement[]) => void;
  onUpdateConfig: (config: NativeFormConfig) => void;
}

export const NativeFormElementContentPanel = ({
  type,
  block,
  formElement,
  onAddClick,
  onUpdateField,
  onUpdateConfig,
}: Props) => {
  if (type === null) {
    return null;
  }

  if (type === 'add') {
    const onAddButtonClick = (elementType: NativeFormElementType) => () => {
      onAddClick(elementType);
    };

    return (
      <div className='w-[271px] rounded-lg bg-neue-journey-bg-90 text-neue-journey-fg flex flex-col pointer-events-auto'>
        <div className='flex-1 text-neue-journey-medium-strong p-4 pb-0'>Add form element</div>
        <div className='relative flex flex-col gap-4 p-4'>
          <span className='text-neue-journey-small -mt-3'>Select the input type you want to add</span>
          <div className='flex gap-2 text-neue-journey-fg'>
            {NativeFormSupportedElementTypes.map((elementType) => (
              <ControlPanelToggleButton
                key={elementType}
                label={capitalize(elementType)}
                onClick={onAddButtonClick(elementType)}
                selected={false}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (type === 'block') {
    const blockContent = block.content as NativeFormBlockContent;
    return (
      <div className='w-[271px] rounded-lg bg-neue-journey-bg-90 text-neue-journey-fg flex flex-col pointer-events-auto'>
        <div className='flex-1 text-neue-journey-medium-strong p-4 pb-0'>Configure form</div>
        <div className='relative flex flex-col gap-4 p-4'>
          <span className='text-neue-journey-small -mt-3'>Form behaviour settings</span>
          <div className='flex gap-2 text-neue-journey-fg'>
            <NativeFormConfigBlockForm {...blockContent.config} onSubmit={onUpdateConfig} />
          </div>
        </div>
      </div>
    );
  }

  if (formElement === null) {
    return null;
  }

  const blockContent = block.content as NativeFormBlockContent;
  const NativeElementConfig = NativeFormElementConfigFormFactory(
    {
      type: formElement.type,
      fields: blockContent.fields,
    },
    onUpdateField
  );

  return (
    <div className='w-[271px] rounded-lg bg-neue-journey-bg-90 text-neue-journey-fg flex flex-col pointer-events-auto'>
      <div className='flex-1 text-neue-journey-medium-strong p-4 pb-0'>Edit {formElement.type}</div>
      <div className='relative flex flex-col gap-4 p-4'>
        <span className='text-neue-journey-small -mt-3'>Configure {formElement.type} behaviour</span>
        <NativeElementConfig {...formElement} />
      </div>
    </div>
  );
};
